// ContentItem.tsx (com suporte a múltiplas listas)
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ThumbsUp, Share, Plus, Check, Info } from 'lucide-react';
import {
  getAllLists,
  addToList,
  removeFromList,
  isInList,
  ContentItem as ContentDataItem
} from '../utils/contentService';

interface ContentItemProps {
  item: ContentDataItem;
  category: string;
}

const ContentItemComponent: React.FC<ContentItemProps> = ({ item, category }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [liked, setLiked] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showListMenu, setShowListMenu] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const allLists = getAllLists();

  const handleAddTo = (listName: string) => {
    const already = isInList(listName, item.id);

    if (already) {
      removeFromList(listName, item.id);
      toast({ title: `Removido de "${listName}"`, description: `"${item.title}" foi removido da lista.` });
    } else {
      addToList(listName, item);
      toast({ title: `Adicionado em "${listName}"`, description: `"${item.title}" foi salvo.` });
    }

    setShowListMenu(false);
  };

  const handleLike = () => {
    setLiked(!liked);
    toast({ title: liked ? "Curtida removida" : "Curtido!", description: item.title });
  };

  const handlePlayClick = () => {
    if (item.embedId) navigate(`/watch/${item.embedId}`);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: item.title,
        text: `Confira ${item.title} na ZezéFlix!`,
        url: window.location.href,
      }).catch((error) => console.log('Erro ao compartilhar', error));
    } else {
      toast({ title: "Link copiado", description: "Copiado para área de transferência" });
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <>
      <div
        className="content-item relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setShowListMenu(false);
        }}
      >
        {!isHovered ? (
          <img src={item.imageSrc} alt={item.title} className="content-image" />
        ) : (
          <video
            src={item.videoSrc || ''}
            className="hover-video"
            muted
            autoPlay
            loop
            playsInline
          />
        )}

        <h2>{item.title}</h2>

        <div className="content-overlay z-10">
          <div className="flex gap-2 relative">
            {item.embedId && (
              <button
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayClick();
                }}
              >
                <span className="material-symbols-outlined text-sm">play_arrow</span>
              </button>
            )}

            {/* Botão de lista */}
            <div className="relative">
              <button
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowListMenu((prev) => !prev);
                }}
              >
                <Plus className="w-4 h-4 text-rustico-brown" />
              </button>

              {/* Menu de listas */}
              {showListMenu && (
                <div className="absolute right-0 mt-1 bg-zinc-900/95 border border-zinc-700 rounded shadow z-50 text-sm w-48">
                  {Object.keys(allLists).map((listName) => (
                    <button
                      key={listName}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddTo(listName);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between"
                    >
                      {listName}
                      {isInList(listName, item.id) && <Check size={14} className="text-green-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <button
              className="w-8 h-8 rounded-full bg-white flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                setShowDialog(true);
              }}
            >
              <Info className="w-4 h-4 text-rustico-brown" />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#141414] border-gray-700 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">{item.title}</DialogTitle>
          </DialogHeader>
          <div className="relative mt-2">
            <img src={item.imageSrc} alt={item.title} className="w-full h-[400px] object-cover rounded-lg" />
          </div>
          <DialogDescription className="text-white">
            <p className="my-4">
              {category === 'shows' && 'Show incrível com músicas que marcaram época.'}
              {category === 'filmes' && 'Um filme emocionante que conta histórias do universo sertanejo.'}
              {category === 'series' && 'Uma série que mostra o dia a dia e a carreira dos artistas sertanejos.'}
              {category === 'minha-lista' && 'Você salvou este item na sua lista.'}
              {!['shows', 'filmes', 'series', 'minha-lista'].includes(category) && 'Conteúdo especial para os fãs do sertanejo.'}
            </p>
            <div className="flex flex-col gap-3 mt-6">
              <div className="flex justify-between">
                <Button onClick={handleLike} variant="ghost" className="text-white flex gap-2 items-center">
                  <ThumbsUp size={18} /> {liked ? 'Curtido' : 'Curtir'}
                </Button>
                <Button onClick={handleShare} variant="ghost" className="text-white flex gap-2 items-center">
                  <Share size={18} /> Compartilhar
                </Button>
              </div>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContentItemComponent;
